import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import VehicleInput from '../components/VehicleInput';

const COMPANY_PHONE = '9014556536';

const Search = () => {
  const [searchParams] = useSearchParams();
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doSearch = async (vn) => {
    setError('');
    setSearchResult(null);
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/search/vehicle/${encodeURIComponent(vn)}`
      );
      setSearchResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'User not found with this vehicle number');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const vehicleParam = searchParams.get('vehicle');
    if (vehicleParam) {
      const vn = vehicleParam.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setVehicleNumber(vn);
      doSearch(vn);
    }
  }, [searchParams]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!vehicleNumber) return;
    doSearch(vehicleNumber);
  };

  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121214] border border-white/10 rounded-2xl p-8"
        >
          <h2 className="text-3xl font-medium mb-2 tracking-tight">Emergency Blood Group Search</h2>
          <p className="text-[#A1A1A6] mb-8">Search by vehicle number to find blood group information</p>

          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex gap-4">
              <VehicleInput
                value={vehicleNumber}
                onChange={setVehicleNumber}
                placeholder="Enter vehicle number (e.g., TS09AB1234)"
                required
                dataTestId="search-vehicle-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="search-vehicle-button"
                className="px-8 py-3 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium transition-all disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

          {error && (
            <div className="bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-4 text-[#FF453A]" data-testid="search-error-message">
              {error}
            </div>
          )}

          {searchResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                <h3 className="text-2xl font-medium mb-6 text-[#FF3B30]">User Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {searchResult.photo && (
                    <div>
                      <img
                        src={searchResult.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${searchResult.photo}` : searchResult.photo}
                        alt={searchResult.name}
                        className="w-32 h-32 object-cover rounded-lg border border-white/20"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-[#A1A1A6] text-sm">Name</p>
                      <p className="text-lg font-medium" data-testid="search-result-name">{searchResult.name}</p>
                    </div>
                    
                    <div>
                      <p className="text-[#A1A1A6] text-sm">Blood Group</p>
                      <p className="text-2xl font-medium text-[#FF3B30]" data-testid="search-result-blood-group">
                        {searchResult.blood_group || 'Not specified'}
                      </p>
                    </div>
                    
                    {searchResult.alt_contact && (
                      <div>
                        <p className="text-[#A1A1A6] text-sm">Alternative Contact</p>
                        <p className="text-lg" data-testid="search-result-alt-contact">{searchResult.alt_contact}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-[#A1A1A6] text-sm">MyGaadiNumber Helpline</p>
                      <a
                        href={`tel:${COMPANY_PHONE}`}
                        data-testid="company-phone-link"
                        className="text-lg font-medium text-[#0A84FF] hover:underline flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {COMPANY_PHONE}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {(searchResult.digital_health_card || searchResult.arogyasri_card || searchResult.ayushman_bharat_card || searchResult.insurance_card || (searchResult.health_profile_files && searchResult.health_profile_files.length > 0)) && (
                <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                  <h4 className="text-xl font-medium mb-4">Health Documents</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {searchResult.digital_health_card && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${searchResult.digital_health_card}`} target="_blank" rel="noreferrer" data-testid="search-health-card" className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#FF3B30]/50 transition-all">
                        <svg className="w-8 h-8 mx-auto mb-2 text-[#32D74B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" /></svg>
                        <p className="text-sm font-medium">Health Card</p>
                        <p className="text-xs text-[#0A84FF] mt-1">View</p>
                      </a>
                    )}
                    {searchResult.arogyasri_card && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${searchResult.arogyasri_card}`} target="_blank" rel="noreferrer" data-testid="search-arogyasri" className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#FF3B30]/50 transition-all">
                        <svg className="w-8 h-8 mx-auto mb-2 text-[#FF9F0A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" /></svg>
                        <p className="text-sm font-medium">Arogyasri</p>
                        <p className="text-xs text-[#0A84FF] mt-1">View</p>
                      </a>
                    )}
                    {searchResult.ayushman_bharat_card && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${searchResult.ayushman_bharat_card}`} target="_blank" rel="noreferrer" data-testid="search-ayushman" className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#FF3B30]/50 transition-all">
                        <svg className="w-8 h-8 mx-auto mb-2 text-[#0A84FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" /></svg>
                        <p className="text-sm font-medium">Ayushman Bharat</p>
                        <p className="text-xs text-[#0A84FF] mt-1">View</p>
                      </a>
                    )}
                    {searchResult.insurance_card && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${searchResult.insurance_card}`} target="_blank" rel="noreferrer" data-testid="search-insurance" className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#FF3B30]/50 transition-all">
                        <svg className="w-8 h-8 mx-auto mb-2 text-[#BF5AF2]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                        <p className="text-sm font-medium">Insurance</p>
                        <p className="text-xs text-[#0A84FF] mt-1">View</p>
                      </a>
                    )}
                    {searchResult.health_profile_files && searchResult.health_profile_files.map((url, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer" data-testid={`search-health-profile-${i}`} className="bg-black/50 border border-white/10 rounded-xl p-4 text-center hover:border-[#FF3B30]/50 transition-all">
                        <svg className="w-8 h-8 mx-auto mb-2 text-[#FF375F]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>
                        <p className="text-sm font-medium">Health Profile {i + 1}</p>
                        <p className="text-xs text-[#0A84FF] mt-1">View</p>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                <h4 className="text-xl font-medium mb-4">Vehicle Details</h4>
                <div className="space-y-3">
                  {searchResult.vehicle1_number && (
                    <div className="flex justify-between">
                      <span className="text-[#A1A1A6]">Vehicle 1:</span>
                      <span className="font-medium">
                        {searchResult.vehicle1_name} - {searchResult.vehicle1_number}
                      </span>
                    </div>
                  )}
                  {searchResult.vehicle2_number && (
                    <div className="flex justify-between">
                      <span className="text-[#A1A1A6]">Vehicle 2:</span>
                      <span className="font-medium">
                        {searchResult.vehicle2_name} - {searchResult.vehicle2_number}
                      </span>
                    </div>
                  )}
                  {searchResult.vehicle3_number && (
                    <div className="flex justify-between">
                      <span className="text-[#A1A1A6]">Vehicle 3:</span>
                      <span className="font-medium">
                        {searchResult.vehicle3_name} - {searchResult.vehicle3_number}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {searchResult.family_members && searchResult.family_members.length > 0 && (
                <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                  <h4 className="text-xl font-medium mb-4">Family Members</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResult.family_members.map((member, index) => (
                      <div key={index} className="bg-black/50 p-4 rounded-lg border border-white/10">
                        {member.photo && (
                          <img
                            src={member.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${member.photo}` : member.photo}
                            alt={member.name}
                            className="w-20 h-20 object-cover rounded-lg mb-3"
                          />
                        )}
                        <p className="font-medium" data-testid={`family-member-${index}-name`}>{member.name}</p>
                        <p className="text-[#FF3B30]" data-testid={`family-member-${index}-blood-group`}>
                          {member.bloodGroup || 'Not specified'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 rounded-lg p-4">
                <p className="text-[#FF9F0A] text-sm">
                  <strong>Disclaimer:</strong> This information is provided for emergency purposes only. MyGaadiNumber is not
                  responsible for the accuracy of this information. Please verify before taking any action.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Search;
